import { CheckCircleFilled, ClockCircleFilled, MessageFilled } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge, Modal } from 'antd';
import caregiversNoAi from 'assets/caregivers-no-ai.png';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import usePreferences from 'hooks/usePreferences';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Platform from 'utils/Platform';
import { Button } from 'components/ui';
import { colors } from 'design';

export const BETTER_NOTIFICATION_REQUEST_TIMING_FLAG = 'better-notification-request-timing';

export type PushNotificationOptInTrigger = 'first_conversation' | 'first_tracking' | 'questionnaire_completed';

type PushNotificationOptInContextValue = {
  requestOptIn: (trigger: PushNotificationOptInTrigger) => void;
  /**
   * Handles tapping a notification suggester on native:
   * - If the OS has never been asked for push permissions, calls requestPermissions()
   *   directly so iOS registers this app as notification-capable.
   * - If the permission is already denied (or granted), opens the app's OS Settings page.
   */
  enableNotifications: () => void;
};

const PushNotificationOptInContext = createContext<PushNotificationOptInContextValue | undefined>(undefined);

export function PushNotificationOptInProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<PushNotificationOptInTrigger | null>(null);
  const processingRef = useRef(false);

  const {
    value: isFlowCompleted,
    set: markFlowCompleted,
    loading: loadingFlowCompleted,
  } = usePreferences(Keys.PUSH_OPT_IN_FLOW_COMPLETED);

  const requestOptIn = useCallback(
    (trigger: PushNotificationOptInTrigger) => {
      if (!Platform.isNative()) return;
      if (!Capacitor.isPluginAvailable('PushNotifications')) return;
      if (!posthog.isFeatureEnabled(BETTER_NOTIFICATION_REQUEST_TIMING_FLAG)) return;
      if (loadingFlowCompleted) return;
      if (isFlowCompleted === true) return;
      if (processingRef.current || open) return;

      processingRef.current = true;
      PushNotifications.checkPermissions()
        .then(status => {
          posthog.capture('$set', {
            $set: { gynger_push_notifications_permission: status.receive },
          });
          if (status.receive === 'granted') {
            void PushNotifications.register();
            markFlowCompleted('true');
            posthog.capture('notifications.permission_already_granted', { trigger });
            return;
          }
          if (status.receive === 'denied') {
            markFlowCompleted('true');
            posthog.capture('notifications.permission_already_denied', { trigger });
            return;
          }
          triggerRef.current = trigger;
          setOpen(true);
          posthog.capture('notifications.permission_prompt_shown', { trigger });
        })
        .finally(() => {
          processingRef.current = false;
        });
    },
    [isFlowCompleted, loadingFlowCompleted, markFlowCompleted, open],
  );

  const enableNotifications = useCallback(() => {
    if (!Platform.isNative() || !Capacitor.isPluginAvailable('PushNotifications')) {
      return;
    }
    PushNotifications.checkPermissions().then(status => {
      if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
        // Never been asked: call requestPermissions so iOS registers the app.
        PushNotifications.requestPermissions().then(result => {
          posthog.capture('$set', {
            $set: { gynger_push_notifications_permission: result.receive },
          });
          if (result.receive === 'granted') {
            markFlowCompleted('true');
            void PushNotifications.register();
          }
          posthog.capture('notifications.enable_notifications_clicked', {
            result: result.receive,
          });
        });
      } else {
        // Already granted or denied: open system settings.
        NativeSettings.open({
          optionAndroid: AndroidSettings.AppNotification,
          optionIOS: IOSSettings.App,
        });
        posthog.capture('notifications.enable_notifications_clicked', { result: 'open_settings' });
      }
    });
  }, [markFlowCompleted]);

  const closeModal = () => setOpen(false);

  const onLater = () => {
    const trigger = triggerRef.current;
    closeModal();
    markFlowCompleted('true');
    if (trigger) {
      posthog.capture('notifications.permission_prompt_dismissed', { trigger });
    }
    triggerRef.current = null;
  };

  const onActivate = async () => {
    const trigger = triggerRef.current;
    closeModal();
    if (!trigger) return;

    posthog.capture('notifications.permission_prompt_confirm_clicked', { trigger });
    const result = await PushNotifications.requestPermissions();
    posthog.capture('$set', {
      $set: { gynger_push_notifications_permission: result.receive },
    });
    markFlowCompleted('true');
    const granted = result.receive === 'granted';
    posthog.capture('notifications.permission_result', {
      trigger,
      result: granted ? 'granted' : 'denied',
    });
    if (granted) {
      await PushNotifications.register();
    }
    triggerRef.current = null;
  };

  return (
    <PushNotificationOptInContext.Provider value={{ requestOptIn, enableNotifications }}>
      {children}
      {Platform.isNative() && Capacitor.isPluginAvailable('PushNotifications') && (
        <Modal
          open={open}
          title={null}
          width="100%"
          style={{ maxWidth: 350 }}
          onCancel={onLater}
          footer={[
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
              <Button variant="link" key="later" onClick={onLater}>
                {t('pushNotificationsOptIn.later')}
              </Button>
              <Button
                variant="primary"
                key="ok"
                onClick={() => {
                  void onActivate();
                }}
              >
                {t('pushNotificationsOptIn.activate')}
              </Button>
            </div>,
          ]}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 24,
              paddingTop: 12,
              paddingBottom: 24,
            }}
          >
            <Badge count={1} color={colors.brand.accentStrong} offset={[-18, 8]}>
              <img
                src={caregiversNoAi}
                alt=""
                style={{
                  width: 80,
                  height: 'auto',
                  borderRadius: 14,
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            </Badge>

            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{t('pushNotificationsOptIn.title')}</div>

            <div
              style={{
                width: '100%',
                maxWidth: 420,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                paddingLeft: 12,
                paddingRight: 12,
              }}
            >
              <p>{t('pushNotificationsOptIn.body')}</p>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  width: 'fit-content',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <MessageFilled style={{ fontSize: 18, color: colors.text.disabled }} />
                  <span>{t('pushNotificationsOptIn.bullets.0')}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <ClockCircleFilled style={{ fontSize: 18, color: colors.text.disabled }} />
                  <span>{t('pushNotificationsOptIn.bullets.1')}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <CheckCircleFilled style={{ fontSize: 18, color: colors.text.disabled }} />
                  <span>{t('pushNotificationsOptIn.bullets.2')}</span>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </PushNotificationOptInContext.Provider>
  );
}

export function usePushNotificationOptIn(): PushNotificationOptInContextValue {
  const ctx = useContext(PushNotificationOptInContext);
  if (!ctx) throw new Error('usePushNotificationOptIn must be used within PushNotificationOptInProvider');
  return ctx;
}
